import { Config } from 'types'
import { useNavigate } from 'react-router-dom'
import { getErrorName, getLocale, getShopId, queryClient, updateShopId, updateShopName, useApiQuery, useAuthentication } from 'utils'
import { planQuery } from './plan.api'

export const useConfig = () => {

    //hooks:
    const { isLoggedIn, logout } = useAuthentication()
    const navigate = useNavigate()
    const shopId = getShopId()

    //values:
    const getBasicConfig = !isLoggedIn || !shopId
    const cacheKey = ['config', getBasicConfig ? null : shopId]

    //a stored shop id the current user has no read access to (e.g. left in storage
    //by another user of this device) is dropped and the layout re-rendered, so the
    //basic config loads and the flow falls through to getShops (same recovery as
    //useCurrentShop's removeShop):
    const recoverFromInaccessibleShop = () => {
        updateShopId(null)
        updateShopName(null)
        navigate(`${location.pathname}${location.search}${location.hash}`)
    }

    return useApiQuery<Config>({
        query: getBasicConfig ? `
            query getConfig{
                getConfig{
                    locale
                }
            }
        ` : `
            query getConfig(
                $id: String
            ){
                getConfig(
                    id: $id
                ){

                    locale
                    isMigratedToMRML
                    features
                    maxApp

                    plan{
                        ${planQuery}
                    }

                    settings{
                        timezone
                    }

                }
            }
        `,
        variables: getBasicConfig ? undefined : {
            id: shopId
        },
        cacheKey,
        noAuth: getBasicConfig,
        onSuccess: json => {
            let config: Config = getBasicConfig ? {
                locale: json.data.getConfig.locale,
                features: [],
                isMigratedToMRML: true,
                maxApp: 0,
                plan: null,
                settings: {
                    timezone: 'UTC'
                }
            } : json.data.getConfig
            const locale = getLocale()
            if(getBasicConfig && locale){
                config = {
                    ...config, // for unauthenticated pages
                    locale
                }
            }
            return config
        },
        onError: json => {
            queryClient.removeQueries(cacheKey)
            const errorName = getErrorName(json)
            const isRecovered = !getBasicConfig && (errorName === 'not_found' || errorName === 'forbidden')
            if(isLoggedIn && !isRecovered){
                logout()
            }
        },
        ...(getBasicConfig ? {} : {
            onNotFound: recoverFromInaccessibleShop,
            onForbidden: recoverFromInaccessibleShop
        })
    })
}
